import { Button } from '@/components/ui/button'
import RenderIf from '@/components/ui/render-if'
import { Text } from '@/components/ui/text'
import { CommonAlertTriangle, CommonDiscord, CommonLogo, CommonLogoIcon, CommonRefresh, CommonReload } from '@/icons'

import { useTranslation } from 'react-i18next'
import { useRouteError } from 'react-router-dom'

const showDetailError = import.meta.env.DEV

const checkNewVersion = (errorMsg: string) => {
  const lazyImportErrorMsg = 'Failed to fetch dynamically imported module'
  return errorMsg.includes(lazyImportErrorMsg)
}

export const RouterErrorElement = () => {
  const { t } = useTranslation()
  const error = useRouteError() as Error
  const isNewVersion = checkNewVersion(error?.message)
  const errorKey = isNewVersion ? 'error.NEW_RELEASE_VERSION' : 'error.PAGE_ERRORS'

  const handleRefresh = () => {
    window.location.reload()
  }

  return (
    <div className='h-screen'>
      <header className='px-4 md:px-6 bg-black-12 h-[72px] relative text-size-16 ring-[1px] md:ring-[0.5px] [@media(max-resolution:1.9dppx)]:ring-[1px] ring-black-12 flex items-center'>
        <nav>
          <div className='md:flex md:items-center hidden cursor-pointer gap-2' onClick={handleRefresh}>
            <CommonLogo className='w-[138px] h-[32px] my-auto' />
            <Text color='black' className='px-1 bg-main text-black rounded-[2px]' size={12}>
              BETA
            </Text>
          </div>
          <div className='block md:hidden'>
            <button className='block md:hidden relative' onClick={handleRefresh}>
              <CommonLogoIcon className='w-10 h-[34px]' />
              <Text
                color='black'
                className='absolute px-1 right-[-34px] top-0 bottom-0 my-auto h-fit bg-main text-black rounded-[2px]'
                size={12}
              >
                BETA
              </Text>
            </button>
          </div>
        </nav>
      </header>
      <div className='w-[504px] h-[calc(100vh-110px)] mx-auto flex items-center justify-center flex-col'>
        <div className='flex items-center flex-col text-center space-y-3'>
          {isNewVersion ? (
            <CommonRefresh className='size-12 text-green-100' />
          ) : (
            <CommonAlertTriangle className='size-12 text-main-100' />
          )}
          <Text size={28}>{t(`${errorKey}.TITLE`)}</Text>
          <Text color='white-48' className='px-6'>
            {t(`${errorKey}.DESCRIPTION`)}
          </Text>
        </div>
        <div className='flex gap-3 items-center w-full justify-center mx-auto mt-8'>
          <Button variant='secondary' size='sm' onClick={() => window.location.reload()}>
            <CommonReload className='size-5' />
            <Text size={14} as='span'>
              {t('error.REFRESH_PAGE')}
            </Text>
          </Button>
          <RenderIf condition={!isNewVersion}>
            <Button variant='secondary' size='sm'>
              <a href='https://discord.gg/optimex' target='_blank' className='flex items-center gap-1 h-full w-full'>
                <CommonDiscord className='size-5' />
                <Text size={14} as='span'>
                  https://discord.gg/optimex
                </Text>
              </a>
            </Button>
          </RenderIf>
        </div>
        <RenderIf condition={showDetailError}>
          <div className='mt-8 flex flex-col gap-4'>
            <Text color='red'> Error: {error?.message}</Text>
            <Text color='white-48'>{error?.stack ? <pre>{error?.stack}</pre> : null}</Text>
          </div>
        </RenderIf>
      </div>
    </div>
  )
}
